// src/tattoo/flash/FlashManagementPage.jsx
//
// /account/studio (primary route) and /account/flash (alias) —
// artist-side "Studio profile" management. Combines three things
// artists need to update regularly into one page so they're not
// hopping between separate URLs:
//
//   1. Studio logo (the brand-mark tile in the public hero).
//   2. Studio gallery (recent work / studio shots — separate from flash).
//   3. Flash gallery (priced pieces available for booking).
//
// Single shop lookup at the top serves all three sections; each
// section then drives its own state + uploads independently. Headers
// re-establish context as the artist scrolls.
//
// Autosave-on-blur for flash card field edits; optimistic UI
// throughout with a quiet refetch on the rare save failure.

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { supabase } from "../lib/supabaseClient";
import {
  listFlashForOwner,
  uploadFlash,
  updateFlash,
  deleteFlash,
  setFlashFeatured,
  unsetFlashFeatured,
  formatPrice,
  parsePriceToCents,
} from "../lib/flash";
import { uploadShopAvatar, clearShopAvatar } from "../lib/shopAvatar";
import {
  listStudioPhotos,
  uploadStudioPhoto,
  deleteStudioPhoto,
  setStudioPhotoFeatured,
  unsetStudioPhotoFeatured,
} from "../lib/studioPhotos";

const ACCEPTED_MIME = ["image/jpeg", "image/png", "image/webp", "image/heic"];

// Translate a raw upload error (Supabase RLS, storage, network, or
// generic) into an actionable message we can show the artist. The old
// behavior was a flat "Upload failed. Try again." — useless when the
// real cause is a lapsed subscription, a network hiccup, or a file
// that's too large, because each has a different remedy.
function friendlyUploadError(err) {
  if (!err) return "Upload failed — please try again.";
  const msg = err.message || String(err);
  const code = err.code || err.statusCode || "";

  if (/row-level security/i.test(msg) || String(code) === "42501") {
    // Should not happen after the flash owner-insert policy is loosened
    // (see 20260818000000_flash_owner_read.sql), but if it does surface
    // — e.g. someone uploading to a shop they don't own — say something
    // specific rather than blaming the network.
    return "You don't have permission to upload here. If your subscription just changed, refresh the page and try again.";
  }
  if (/network|failed to fetch|load failed/i.test(msg)) {
    return "Couldn't reach the server — check your connection and try again.";
  }
  if (/payload too large|too large|413/i.test(msg) || String(code) === "413") {
    return "That image is too large. Please try one under 15 MB.";
  }
  if (/not authenticated|jwt|token|session/i.test(msg)) {
    return "Your session expired. Please sign in again and retry.";
  }
  // Fall back to the raw error text — noisy but honest, and far more
  // useful for debugging than "try again."
  return msg;
}
const MAX_BYTES = 15 * 1024 * 1024; // 15 MB — flash sheets run larger than studio shots

const STATUS_OPTIONS = [
  { value: "available", label: "Available" },
  { value: "held", label: "Held" },
  { value: "sold", label: "Sold" },
  { value: "archived", label: "Archived (hidden)" },
];

export default function FlashManagementPage() {
  const navigate = useNavigate();

  const [shopId, setShopId] = useState(null);
  const [shopName, setShopName] = useState("");
  const [shopSlug, setShopSlug] = useState(null);
  const [shopCurrency, setShopCurrency] = useState("CAD");
  const [shopAvatarUrl, setShopAvatarUrl] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);

  // Studio gallery (separate feature from flash — recent work and
  // studio shots that render in the "Studio gallery" section of the
  // public artist page).
  const [studioPhotos, setStudioPhotos] = useState([]);
  const [loadingStudioPhotos, setLoadingStudioPhotos] = useState(false);
  const [studioUploading, setStudioUploading] = useState(false);
  const [studioDragOver, setStudioDragOver] = useState(false);
  const [loadingShop, setLoadingShop] = useState(true);
  const [shopError, setShopError] = useState(null);

  const [flash, setFlash] = useState([]);
  const [loadingFlash, setLoadingFlash] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Resolve the signed-in user's shop. Same owner_user_id-then-email
  // pattern the gallery page uses.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingShop(true);
        setShopError(null);

        const {
          data: { user },
          error: userErr,
        } = await supabase.auth.getUser();

        if (userErr || !user) {
          if (!cancelled) setShopError("You need to be signed in.");
          return;
        }

        let shop = null;
        const { data: byOwner } = await supabase
          .from("shops")
          .select("id, display_name, name, slug, currency, avatar_url")
          .eq("owner_user_id", user.id)
          .maybeSingle();

        if (byOwner) shop = byOwner;
        else {
          const { data: byEmail } = await supabase
            .from("shops")
            .select("id, display_name, name, slug, currency, avatar_url")
            .eq("email", user.email)
            .maybeSingle();
          if (byEmail) shop = byEmail;
        }

        if (cancelled) return;

        if (!shop) {
          setShopError(
            "You don't have a studio profile yet. Set one up in the editor first."
          );
          return;
        }

        setShopId(shop.id);
        setShopName(shop.display_name || shop.name || "your studio");
        setShopSlug(shop.slug || null);
        setShopCurrency(shop.currency || "CAD");
        setShopAvatarUrl(shop.avatar_url || null);
      } catch (e) {
        console.error("[flash mgmt] shop lookup error", e);
        if (!cancelled) setShopError("Couldn't load your studio.");
      } finally {
        if (!cancelled) setLoadingShop(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load studio gallery photos once the shop's known.
  useEffect(() => {
    if (!shopId) return;
    let cancelled = false;
    (async () => {
      setLoadingStudioPhotos(true);
      const rows = await listStudioPhotos(shopId);
      if (!cancelled) {
        setStudioPhotos(rows);
        setLoadingStudioPhotos(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shopId]);

  // Load flash once we have the shop.
  useEffect(() => {
    if (!shopId) return;
    let cancelled = false;
    (async () => {
      setLoadingFlash(true);
      const rows = await listFlashForOwner(shopId);
      if (!cancelled) {
        setFlash(rows);
        setLoadingFlash(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shopId]);

  // ---------- Studio gallery upload + delete ----------

  // Cap matches the studio-photos bucket (10 MB) — separate constant from
  // MAX_BYTES because flash is allowed slightly larger sheets.
  const STUDIO_MAX_BYTES = 10 * 1024 * 1024;

  async function handleStudioFiles(fileList) {
    if (!shopId) return;
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;

    const valid = [];
    for (const f of files) {
      if (f.size > STUDIO_MAX_BYTES) {
        toast.error(`${f.name} is over 10 MB — skipped.`);
        continue;
      }
      if (f.type && !ACCEPTED_MIME.includes(f.type)) {
        toast.error(`${f.name}: ${f.type} isn't a supported format — skipped.`);
        continue;
      }
      valid.push(f);
    }
    if (!valid.length) return;

    setStudioUploading(true);
    let okCount = 0;
    let failCount = 0;
    let firstError = null;
    for (const f of valid) {
      try {
        const row = await uploadStudioPhoto(shopId, f);
        setStudioPhotos((prev) => [...prev, row]);
        okCount++;
      } catch (e) {
        console.error("[studio mgmt] upload failed", f.name, e);
        if (!firstError) firstError = e;
        failCount++;
      }
    }
    setStudioUploading(false);

    if (okCount > 0) {
      toast.success(
        okCount === 1 ? "Photo uploaded." : `${okCount} photos uploaded.`
      );
    }
    if (failCount > 0) {
      const reason = friendlyUploadError(firstError);
      toast.error(
        failCount === 1
          ? reason
          : `${failCount} uploads failed — ${reason}`,
        { duration: 6000 }
      );
    }
  }

  // Toggle the featured hero for the studio gallery. Mirrors
  // handleFeaturedToggle below (for flash) — optimistic update, one
  // featured per shop, rolled back on server error.
  const handleStudioFeaturedToggle = useCallback(
    async (id, currentlyFeatured) => {
      if (!id) return;
      // Optimistic: flip locally, then reconcile with the server.
      const prevState = studioPhotos;
      if (currentlyFeatured) {
        setStudioPhotos((prev) =>
          prev.map((p) => (p.id === id ? { ...p, is_featured: false } : p))
        );
        try {
          await unsetStudioPhotoFeatured(id);
          toast.success("Featured photo cleared.");
        } catch (e) {
          console.error("[studio mgmt] unsetFeatured failed", e);
          setStudioPhotos(prevState);
          toast.error("Couldn't clear featured photo. Try again.");
        }
      } else {
        setStudioPhotos((prev) =>
          prev.map((p) => ({ ...p, is_featured: p.id === id }))
        );
        try {
          await setStudioPhotoFeatured(id);
          toast.success("Featured photo updated — shows on your hero.");
        } catch (e) {
          console.error("[studio mgmt] setFeatured failed", e);
          setStudioPhotos(prevState);
          toast.error("Couldn't set featured photo. Try again.");
        }
      }
    },
    [studioPhotos]
  );

  async function handleStudioDelete(photo) {
    if (!photo?.id) return;
    if (
      !window.confirm(
        "Delete this photo? It'll disappear from your public studio page."
      )
    ) {
      return;
    }
    try {
      await deleteStudioPhoto(photo.id);
      setStudioPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      toast.success("Photo deleted.");
    } catch (e) {
      console.error("[studio mgmt] delete failed", e);
      toast.error("Couldn't delete that photo. Try again.");
    }
  }

  function onStudioDrop(e) {
    e.preventDefault();
    setStudioDragOver(false);
    if (e.dataTransfer?.files?.length) {
      handleStudioFiles(e.dataTransfer.files);
    }
  }
  function onStudioDragOver(e) {
    e.preventDefault();
    setStudioDragOver(true);
  }
  function onStudioDragLeave() {
    setStudioDragOver(false);
  }

  // ---------- Flash upload ----------

  async function handleFiles(fileList) {
    if (!shopId) return;
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;

    const valid = [];
    for (const f of files) {
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name} is over 15 MB — skipped.`);
        continue;
      }
      if (f.type && !ACCEPTED_MIME.includes(f.type)) {
        toast.error(
          `${f.name}: ${f.type} isn't a supported format — skipped.`
        );
        continue;
      }
      valid.push(f);
    }
    if (!valid.length) return;

    setUploading(true);
    let okCount = 0;
    let failCount = 0;
    // Capture the FIRST failure so we can show its actual cause to the
    // user. If every failure shares the same cause (common when the
    // issue is systemic — bad permission, no network, session expired),
    // this reads correctly; if causes vary, we still show one useful
    // message and log the rest.
    let firstError = null;
    for (const f of valid) {
      try {
        const row = await uploadFlash(shopId, f, {
          // Seed title from filename so there's something to edit. Strip
          // extension + replace separators with spaces.
          title: f.name
            .replace(/\.[^.]+$/, "")
            .replace(/[_\-]+/g, " ")
            .trim(),
        });
        setFlash((prev) => [row, ...prev]);
        okCount++;
      } catch (e) {
        console.error("[flash mgmt] upload failed", f.name, e);
        if (!firstError) firstError = e;
        failCount++;
      }
    }
    setUploading(false);

    if (okCount > 0) {
      toast.success(
        okCount === 1
          ? "Flash uploaded — add a price + size below."
          : `${okCount} pieces uploaded — add prices below.`
      );
    }
    if (failCount > 0) {
      const reason = friendlyUploadError(firstError);
      toast.error(
        failCount === 1
          ? reason
          : `${failCount} uploads failed — ${reason}`,
        { duration: 6000 }
      );
    }
  }

  // ---------- Per-row callbacks (memoised so FlashRow doesn't re-render needlessly) ----------

  const handleRowUpdate = useCallback(async (id, patch) => {
    // Optimistic local update.
    setFlash((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...patch } : f))
    );
    try {
      const updated = await updateFlash(id, patch);
      // Reconcile with server-returned row (covers updated_at etc).
      setFlash((prev) => prev.map((f) => (f.id === id ? updated : f)));
    } catch (e) {
      console.error("[flash mgmt] update failed", e);
      toast.error("Couldn't save that change.");
      // On failure, refetch to recover — simpler than tracking each
      // field's pre-edit value.
      const fresh = await listFlashForOwner(shopId);
      setFlash(fresh);
    }
  }, [shopId]);

  // Featured toggle uses dedicated set/unset functions rather than the
  // generic updateFlash path because the underlying partial unique
  // index requires us to clear any existing featured row in the same
  // shop before setting a new one.
  const handleFeaturedToggle = useCallback(async (id, currentlyFeatured) => {
    if (currentlyFeatured) {
      // Optimistic: just clear this one.
      setFlash((prev) =>
        prev.map((f) => (f.id === id ? { ...f, is_featured: false } : f))
      );
      try {
        await unsetFlashFeatured(id);
      } catch (e) {
        console.error("[flash mgmt] unset featured failed", e);
        toast.error("Couldn't update featured piece.");
        const fresh = await listFlashForOwner(shopId);
        setFlash(fresh);
      }
    } else {
      // Optimistic: this one becomes featured, all others lose it.
      setFlash((prev) =>
        prev.map((f) => ({ ...f, is_featured: f.id === id }))
      );
      try {
        await setFlashFeatured(id);
        toast.success("Featured piece updated — shows on your hero.");
      } catch (e) {
        console.error("[flash mgmt] set featured failed", e);
        toast.error("Couldn't update featured piece.");
        const fresh = await listFlashForOwner(shopId);
        setFlash(fresh);
      }
    }
  }, [shopId]);

  // ---------- Logo upload ----------

  async function handleAvatarFile(file) {
    if (!file || !shopId) return;
    setAvatarUploading(true);
    try {
      const url = await uploadShopAvatar(shopId, file);
      setShopAvatarUrl(url);
      toast.success("Studio logo updated.");
    } catch (e) {
      console.error("[flash mgmt] avatar upload failed", e);
      toast.error("Couldn't upload that logo. Try a different image.");
    } finally {
      setAvatarUploading(false);
    }
  }

  async function handleAvatarRemove() {
    if (!shopId) return;
    if (!window.confirm("Remove your studio logo? The hero will go back to your initials.")) {
      return;
    }
    const snapshot = shopAvatarUrl;
    setShopAvatarUrl(null);
    try {
      await clearShopAvatar(shopId);
      toast.success("Logo removed.");
    } catch (e) {
      console.error("[flash mgmt] avatar clear failed", e);
      toast.error("Couldn't remove the logo. Try again.");
      setShopAvatarUrl(snapshot);
    }
  }

  const handleRowDelete = useCallback(async (id) => {
    if (
      !window.confirm(
        "Delete this flash? It'll disappear from your public page and the gallery."
      )
    ) {
      return;
    }
    // Optimistic remove.
    const snapshot = flash;
    setFlash((prev) => prev.filter((f) => f.id !== id));
    try {
      await deleteFlash(id);
      toast.success("Flash deleted.");
    } catch (e) {
      console.error("[flash mgmt] delete failed", e);
      toast.error("Couldn't delete that piece. Try again.");
      setFlash(snapshot);
    }
  }, [flash]);

  // ---------- Drop handlers ----------

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) {
      handleFiles(e.dataTransfer.files);
    }
  }
  function onDragOver(e) {
    e.preventDefault();
    setDragOver(true);
  }
  function onDragLeave() {
    setDragOver(false);
  }

  // ---------- Render ----------

  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0a", color: "#f5f5f5" }}>
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          padding: "28px 16px 96px",
          display: "grid",
          gap: 24,
        }}
      >
        {/* Header */}
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => navigate("/app")}
              style={pillButtonStyle}
            >
              ← Back to editor
            </button>
            {shopSlug && (
              <a
                href={`/artists/${shopSlug}`}
                target="_blank"
                rel="noopener noreferrer"
                style={accentPillStyle}
              >
                View public page →
              </a>
            )}
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
            {shopName || ""}
          </div>
        </header>

        <div style={{ display: "grid", gap: 6 }}>
          <h1 style={{ margin: 0, fontSize: 26, lineHeight: 1.1 }}>
            Studio Images
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              color: "rgba(255,255,255,0.62)",
              maxWidth: 680,
              lineHeight: 1.6,
            }}
          >
            Everything that shows on your public artist page: your studio
            logo, recent-work photos, and your flash gallery. Update any
            of these any time — changes go live on tattune.ca within
            seconds.
          </p>
        </div>

        {shopError && (
          <div role="alert" style={errorBoxStyle}>
            {shopError}
          </div>
        )}

        {!shopError && shopId && (
          <>
            {/* Studio logo / brand mark — a single image that replaces the
                initials-on-gradient tile in the artist page hero. Small
                preview + upload/replace/remove controls in one row. */}
            <div style={logoRowStyle}>
              <div style={logoPreviewWrapStyle}>
                {shopAvatarUrl ? (
                  <img
                    src={shopAvatarUrl}
                    alt="Studio logo"
                    style={logoPreviewImgStyle}
                  />
                ) : (
                  <div style={logoPlaceholderStyle}>
                    {(shopName || "T").trim().charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={logoLabelStyle}>Studio logo</div>
                <div style={logoHintStyle}>
                  Replaces the initials tile in your public hero. Square
                  PNG / SVG works best — up to 2 MB.
                </div>
              </div>
              <label style={logoUploadBtnStyle}>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/svg+xml"
                  style={{ display: "none" }}
                  disabled={avatarUploading}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (file) handleAvatarFile(file);
                  }}
                />
                {avatarUploading
                  ? "Uploading…"
                  : shopAvatarUrl
                  ? "Replace"
                  : "Upload"}
              </label>
              {shopAvatarUrl && !avatarUploading && (
                <button
                  type="button"
                  onClick={handleAvatarRemove}
                  style={logoRemoveBtnStyle}
                >
                  Remove
                </button>
              )}
            </div>

            {/* ===== STUDIO GALLERY SECTION =====
                Recent work + studio shots. Separate from flash — these
                are portfolio context, not bookable pieces. Drop zone +
                grid of square thumbs with delete buttons. */}
            <div style={sectionHeaderStyle}>
              <h2 style={sectionTitleStyle}>Studio gallery</h2>
              <div style={sectionCountStyle}>
                {studioPhotos.length}{" "}
                {studioPhotos.length === 1 ? "photo" : "photos"}
              </div>
            </div>

            <label
              onDrop={onStudioDrop}
              onDragOver={onStudioDragOver}
              onDragLeave={onStudioDragLeave}
              style={{
                display: "block",
                borderRadius: 18,
                border: studioDragOver
                  ? "2px dashed rgba(162,51,43,0.7)"
                  : "2px dashed rgba(255,255,255,0.15)",
                background: studioDragOver
                  ? "rgba(162,51,43,0.08)"
                  : "rgba(255,255,255,0.02)",
                padding: "22px 18px",
                textAlign: "center",
                cursor: studioUploading ? "wait" : "pointer",
                transition:
                  "background 120ms ease, border-color 120ms ease",
              }}
            >
              <input
                type="file"
                accept={ACCEPTED_MIME.join(",")}
                multiple
                style={{ display: "none" }}
                disabled={studioUploading}
                onChange={(e) => {
                  handleStudioFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              <div style={{ fontSize: 15, fontWeight: 600 }}>
                {studioUploading
                  ? "Uploading…"
                  : "Drop studio photos here, or click to pick"}
              </div>
              <div
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  color: "rgba(255,255,255,0.5)",
                }}
              >
                JPEG, PNG, WebP, or HEIC. Up to 10 MB each.
              </div>
            </label>

            {loadingStudioPhotos ? (
              <div style={statusLineStyle}>Loading your studio photos…</div>
            ) : studioPhotos.length === 0 ? (
              <div style={statusLineStyle}>
                No studio photos yet. Drop some above — recent work, your
                space, your team.
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: 12,
                }}
              >
                {studioPhotos.map((photo) => (
                  <div
                    key={photo.id}
                    style={{
                      position: "relative",
                      aspectRatio: "4 / 3",
                      background: "rgba(0,0,0,0.25)",
                      borderRadius: 14,
                      overflow: "hidden",
                      border: photo.is_featured
                        ? "1px solid rgba(249,168,38,0.6)"
                        : "1px solid rgba(255,255,255,0.08)",
                      boxShadow: photo.is_featured
                        ? "0 0 0 2px rgba(249,168,38,0.18)"
                        : "none",
                    }}
                  >
                    <img
                      src={photo.url}
                      alt={photo.caption || "Studio photo"}
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />

                    {/* Feature toggle — top-left. When featured, this
                        photo becomes the hero on the public artist
                        page (see ArtistPublicPage.jsx precedence). At
                        most one studio photo per shop can be featured. */}
                    <button
                      type="button"
                      onClick={() =>
                        handleStudioFeaturedToggle(photo.id, photo.is_featured)
                      }
                      style={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        fontSize: 11,
                        color: photo.is_featured
                          ? "#0a0a0a"
                          : "rgba(255,255,255,0.9)",
                        background: photo.is_featured
                          ? "rgba(249,168,38,0.95)"
                          : "rgba(0,0,0,0.7)",
                        border: photo.is_featured
                          ? "1px solid rgba(249,168,38,1)"
                          : "1px solid rgba(255,255,255,0.25)",
                        borderRadius: 999,
                        padding: "4px 10px",
                        cursor: "pointer",
                        backdropFilter: "blur(4px)",
                        fontWeight: photo.is_featured ? 600 : 500,
                      }}
                      title={
                        photo.is_featured
                          ? "This is your featured hero photo — click to unset."
                          : "Set as the featured hero photo on your public page."
                      }
                    >
                      {photo.is_featured ? "★ Featured hero" : "☆ Set as featured"}
                    </button>

                    <button
                      type="button"
                      onClick={() => handleStudioDelete(photo)}
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        fontSize: 11,
                        color: "rgba(255,180,180,0.92)",
                        background: "rgba(0,0,0,0.7)",
                        border: "1px solid rgba(220,38,38,0.5)",
                        borderRadius: 999,
                        padding: "4px 10px",
                        cursor: "pointer",
                        backdropFilter: "blur(4px)",
                      }}
                      title="Delete this photo"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* ===== FLASH SECTION ===== */}
            <div style={{ ...sectionHeaderStyle, marginTop: 12 }}>
              <h2 style={sectionTitleStyle}>Flash gallery</h2>
              <div style={sectionCountStyle}>
                {flash.length}{" "}
                {flash.length === 1 ? "piece" : "pieces"}
                {shopCurrency ? ` · ${shopCurrency}` : ""}
              </div>
            </div>

            {/* Drop / pick zone */}
            <label
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              style={{
                display: "block",
                borderRadius: 18,
                border: dragOver
                  ? "2px dashed rgba(162,51,43,0.7)"
                  : "2px dashed rgba(255,255,255,0.15)",
                background: dragOver
                  ? "rgba(162,51,43,0.08)"
                  : "rgba(255,255,255,0.02)",
                padding: "26px 18px",
                textAlign: "center",
                cursor: uploading ? "wait" : "pointer",
                transition:
                  "background 120ms ease, border-color 120ms ease",
              }}
            >
              <input
                type="file"
                accept={ACCEPTED_MIME.join(",")}
                multiple
                style={{ display: "none" }}
                disabled={uploading}
                onChange={(e) => {
                  handleFiles(e.target.files);
                  // Allow re-selecting the same file later.
                  e.target.value = "";
                }}
              />
              <div style={{ fontSize: 15, fontWeight: 600 }}>
                {uploading
                  ? "Uploading…"
                  : "Drop flash here, or click to pick"}
              </div>
              <div
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  color: "rgba(255,255,255,0.5)",
                }}
              >
                JPEG, PNG, WebP, or HEIC. Up to 15 MB each.
              </div>
            </label>

            {/* List */}
            {loadingFlash ? (
              <div style={statusLineStyle}>Loading your flash…</div>
            ) : flash.length === 0 ? (
              <div style={statusLineStyle}>
                No flash uploaded yet. Drop a piece above to get started.
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fill, minmax(280px, 1fr))",
                  gap: 14,
                  gridAutoRows: "1fr",
                }}
              >
                {flash.map((item) => (
                  <FlashRow
                    key={item.id}
                    flash={item}
                    currency={shopCurrency}
                    onUpdate={handleRowUpdate}
                    onDelete={handleRowDelete}
                    onFeaturedToggle={handleFeaturedToggle}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// FlashRow — single editable card
// =============================================================================

function FlashRow({ flash, currency, onUpdate, onDelete, onFeaturedToggle }) {
  // Local drafts so typing doesn't fire a save on every keystroke.
  const [title, setTitle] = useState(flash.title || "");
  const [priceText, setPriceText] = useState(centsToInput(flash.price_cents));
  const [size, setSize] = useState(flash.size_label || "");
  const [status, setStatus] = useState(flash.status || "available");
  const [tagsText, setTagsText] = useState(
    Array.isArray(flash.style_tags) ? flash.style_tags.join(", ") : ""
  );
  const [oneOfOne, setOneOfOne] = useState(!!flash.is_one_of_one);

  // Sync drafts if the parent row changes underneath us (e.g. after a
  // recover-from-failed-update refetch).
  useEffect(() => {
    setTitle(flash.title || "");
    setPriceText(centsToInput(flash.price_cents));
    setSize(flash.size_label || "");
    setStatus(flash.status || "available");
    setTagsText(
      Array.isArray(flash.style_tags) ? flash.style_tags.join(", ") : ""
    );
    setOneOfOne(!!flash.is_one_of_one);
  }, [flash.id, flash.updated_at]);

  // Save helpers — fire only when the value actually changed vs the
  // last-persisted row.
  function commitTitle() {
    const next = title.trim() || null;
    if (next !== (flash.title || null)) onUpdate(flash.id, { title: next });
  }
  function commitPrice() {
    const next = parsePriceToCents(priceText);
    if (next !== (flash.price_cents ?? null)) {
      onUpdate(flash.id, { price_cents: next });
    }
    setPriceText(centsToInput(next));
  }
  function commitSize() {
    const next = size.trim() || null;
    if (next !== (flash.size_label || null)) {
      onUpdate(flash.id, { size_label: next });
    }
  }
  function commitStatus(value) {
    if (value !== flash.status) onUpdate(flash.id, { status: value });
  }
  function commitTags() {
    const next = tagsText
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    const current = Array.isArray(flash.style_tags) ? flash.style_tags : [];
    if (JSON.stringify(next) !== JSON.stringify(current)) {
      onUpdate(flash.id, { style_tags: next.length ? next : null });
    }
  }
  function commitOneOfOne(value) {
    if (value !== !!flash.is_one_of_one) {
      onUpdate(flash.id, { is_one_of_one: value });
    }
  }

  return (
    <div style={cardStyle}>
      {/* Image */}
      <div style={cardImageWrapStyle}>
        {flash.url ? (
          <img
            src={flash.url}
            alt={title || "Flash"}
            style={cardImageStyle}
          />
        ) : (
          <div style={cardImagePlaceholderStyle}>No image</div>
        )}
        {flash.is_one_of_one && (
          <div style={oneOfOneBadgeStyle}>1/1</div>
        )}
        {flash.is_featured && (
          <div style={featuredBadgeStyle}>★ Featured</div>
        )}
      </div>

      {/* Editable fields */}
      <div style={cardBodyStyle}>
        {/* Featured toggle — high-signal, sits above the title so it's
            obvious which piece is set as the artist's hero image. */}
        <button
          type="button"
          onClick={() => onFeaturedToggle?.(flash.id, flash.is_featured)}
          style={
            flash.is_featured
              ? featuredButtonActiveStyle
              : featuredButtonStyle
          }
          title={
            flash.is_featured
              ? "This piece is your featured hero. Click to remove."
              : "Make this your featured hero on your public page."
          }
        >
          {flash.is_featured ? "★ Featured hero" : "☆ Set as featured"}
        </button>

        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={commitTitle}
          placeholder="Title (e.g. Skull)"
          style={inputBigStyle}
        />

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 110px", minWidth: 110 }}>
            <Label>Price</Label>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={prefixStyle}>{currencyPrefix(currency)}</span>
              <input
                value={priceText}
                onChange={(e) => setPriceText(e.target.value)}
                onBlur={commitPrice}
                placeholder="—"
                inputMode="decimal"
                style={inputSmallStyle}
              />
            </div>
            {!priceText && (
              <div style={hintStyle}>Shows as "Price on request"</div>
            )}
          </div>

          <div style={{ flex: "1 1 110px", minWidth: 110 }}>
            <Label>Size</Label>
            <input
              value={size}
              onChange={(e) => setSize(e.target.value)}
              onBlur={commitSize}
              placeholder='e.g. 3 in'
              style={inputSmallStyle}
            />
          </div>
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          <div>
            <Label>Status</Label>
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                commitStatus(e.target.value);
              }}
              style={selectStyle}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label>Style tags</Label>
            <input
              value={tagsText}
              onChange={(e) => setTagsText(e.target.value)}
              onBlur={commitTags}
              placeholder="traditional, dark, skulls"
              style={inputSmallStyle}
            />
          </div>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              color: "rgba(255,255,255,0.78)",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <input
              type="checkbox"
              checked={oneOfOne}
              onChange={(e) => {
                setOneOfOne(e.target.checked);
                commitOneOfOne(e.target.checked);
              }}
              style={{ width: 16, height: 16, cursor: "pointer" }}
            />
            One-of-one
          </label>
        </div>

        <div
          style={{
            marginTop: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            paddingTop: 8,
            borderTop: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}>
            {formatPrice(flash.price_cents, currency)}
            {flash.size_label ? ` · ${flash.size_label}` : ""}
          </div>
          <button
            type="button"
            onClick={() => onDelete(flash.id)}
            style={deleteButtonStyle}
            title="Delete this piece"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        color: "rgba(255,255,255,0.5)",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        marginBottom: 4,
      }}
    >
      {children}
    </div>
  );
}

// ---------- helpers ----------

function centsToInput(cents) {
  if (cents == null) return "";
  const n = Number(cents);
  if (!Number.isFinite(n)) return "";
  const dollars = n / 100;
  return Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
}

function currencyPrefix(code) {
  switch ((code || "CAD").toUpperCase()) {
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    default:
      return "$";
  }
}

// ---------- styles ----------

const pillButtonStyle = {
  fontSize: 13,
  color: "rgba(255,255,255,0.78)",
  background: "transparent",
  border: "1px solid rgba(255,255,255,0.18)",
  padding: "6px 12px",
  borderRadius: 999,
  cursor: "pointer",
};

const accentPillStyle = {
  fontSize: 13,
  color: "rgba(255,255,255,0.92)",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.22)",
  padding: "6px 12px",
  borderRadius: 999,
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
};

const errorBoxStyle = {
  borderRadius: 14,
  padding: 14,
  background: "rgba(220,38,38,0.12)",
  border: "1px solid rgba(220,38,38,0.4)",
  color: "#ffb4b4",
  fontSize: 14,
};

const statusLineStyle = {
  fontSize: 13,
  color: "rgba(255,255,255,0.6)",
  padding: 14,
};

const cardStyle = {
  display: "flex",
  flexDirection: "column",
  background: "#141414",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 16,
  overflow: "hidden",
};

const cardImageWrapStyle = {
  position: "relative",
  width: "100%",
  aspectRatio: "1 / 1",
  background: "#fff", // Flash-sheet white for visual consistency.
};

const cardImageStyle = {
  width: "100%",
  height: "100%",
  objectFit: "contain",
  display: "block",
};

const cardImagePlaceholderStyle = {
  width: "100%",
  height: "100%",
  display: "grid",
  placeItems: "center",
  color: "rgba(0,0,0,0.4)",
  fontSize: 13,
};

const oneOfOneBadgeStyle = {
  position: "absolute",
  top: 8,
  right: 8,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.04em",
  color: "#fff",
  background: "rgba(0,0,0,0.65)",
  padding: "3px 8px",
  borderRadius: 999,
  backdropFilter: "blur(4px)",
};

const cardBodyStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  padding: 14,
  flex: 1,
};

const inputBigStyle = {
  fontSize: 14,
  fontWeight: 600,
  color: "#fff",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.10)",
  borderRadius: 8,
  padding: "8px 10px",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

const inputSmallStyle = {
  fontSize: 13,
  color: "#fff",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.10)",
  borderRadius: 8,
  padding: "7px 10px",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

const selectStyle = {
  ...inputSmallStyle,
  appearance: "none",
  WebkitAppearance: "none",
  paddingRight: 28,
};

const prefixStyle = {
  fontSize: 13,
  color: "rgba(255,255,255,0.5)",
  paddingRight: 2,
};

const hintStyle = {
  marginTop: 4,
  fontSize: 11,
  color: "rgba(255,255,255,0.4)",
};

const deleteButtonStyle = {
  fontSize: 12,
  color: "rgba(255,180,180,0.85)",
  background: "transparent",
  border: "1px solid rgba(220,38,38,0.35)",
  borderRadius: 999,
  padding: "5px 10px",
  cursor: "pointer",
};

// Featured badge overlaid on the image when this piece is the artist's
// hero. Gold-leaning palette to read as "premium / pinned".
const featuredBadgeStyle = {
  position: "absolute",
  bottom: 8,
  left: 8,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.04em",
  color: "#1a1305",
  background: "rgba(250,204,21,0.95)",
  padding: "3px 9px",
  borderRadius: 999,
};

const featuredButtonStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: "rgba(255,255,255,0.78)",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: 8,
  padding: "8px 10px",
  cursor: "pointer",
  textAlign: "center",
};

const featuredButtonActiveStyle = {
  ...featuredButtonStyle,
  color: "#1a1305",
  background: "rgba(250,204,21,0.95)",
  borderColor: "rgba(250,204,21,1)",
};

// Studio logo upload row — a compact, single-line block above the
// flash drop zone.
const logoRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 14,
  padding: "14px 16px",
  borderRadius: 14,
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.08)",
  flexWrap: "wrap",
};

const logoPreviewWrapStyle = {
  width: 56,
  height: 56,
  borderRadius: 12,
  overflow: "hidden",
  border: "1px solid rgba(255,255,255,0.10)",
  background: "#1a1a1a",
  flex: "0 0 auto",
};

const logoPreviewImgStyle = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
};

const logoPlaceholderStyle = {
  width: "100%",
  height: "100%",
  display: "grid",
  placeItems: "center",
  color: "rgba(255,255,255,0.55)",
  fontSize: 22,
  fontWeight: 700,
  letterSpacing: "0.02em",
};

const logoLabelStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: "#fff",
};

const logoHintStyle = {
  marginTop: 2,
  fontSize: 12,
  color: "rgba(255,255,255,0.55)",
  lineHeight: 1.4,
};

const logoUploadBtnStyle = {
  fontSize: 13,
  fontWeight: 500,
  color: "#fff",
  background: "rgba(162,51,43,0.95)",
  border: "1px solid rgba(162,51,43,1)",
  borderRadius: 999,
  padding: "8px 14px",
  cursor: "pointer",
};

const logoRemoveBtnStyle = {
  fontSize: 13,
  color: "rgba(255,180,180,0.85)",
  background: "transparent",
  border: "1px solid rgba(220,38,38,0.35)",
  borderRadius: 999,
  padding: "8px 14px",
  cursor: "pointer",
};

// Inline section headers — the page now has three (Logo as the top row,
// then "Studio gallery" and "Flash gallery"). Reasserts context as the
// artist scrolls.
const sectionHeaderStyle = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: 12,
  marginTop: 8,
  paddingBottom: 6,
  borderBottom: "1px solid rgba(255,255,255,0.08)",
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: 18,
  fontWeight: 700,
  letterSpacing: "-0.01em",
  color: "#fff",
};

const sectionCountStyle = {
  fontSize: 12,
  color: "rgba(255,255,255,0.5)",
};
